import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Input, Result, Space, Tag, message } from 'antd';
import { useAuth } from '../context/AuthContext';
import { canViewOperationLogs } from '../utils/permissions';
import { shipxyConfigApi } from '../api/kfc';

/**
 * 船讯网 shipxy 密钥配置（031）：配置后海运船位抓取优先走 shipxy API（国内可达），
 * 未配置时回落 MyShipTracking / VesselFinder 网页源（海外服务器可用）。
 */
const ShipxyConfigPage: React.FC = () => {
  const { session } = useAuth();
  const allowed = canViewOperationLogs(session.identity?.traits);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [maskedKey, setMaskedKey] = useState<string | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    if (!allowed) return;
    setLoading(true);
    shipxyConfigApi
      .get()
      .then((res) => {
        setConfigured(Boolean(res.data?.configured));
        setMaskedKey(res.data?.apiKey ?? null);
      })
      .catch((e: { response?: { data?: { message?: string }; status?: number } }) => {
        if (e.response?.status === 403) message.error('无权查看');
        else message.error(e.response?.data?.message || '加载失败');
      })
      .finally(() => setLoading(false));
  }, [allowed]);

  if (!allowed) {
    return <Result status="403" title="无权访问" subTitle="仅管理员可配置船讯网密钥" />;
  }

  const onSave = () => {
    form.validateFields().then((values: { apiKey?: string }) => {
      setSaving(true);
      shipxyConfigApi
        .put({ configured, apiKey: values.apiKey ?? '' })
        .then((res) => {
          message.success('已保存');
          setConfigured(Boolean(res.data?.configured));
          setMaskedKey(res.data?.apiKey ?? null);
          form.resetFields();
        })
        .catch((e: { response?: { data?: { message?: string } } }) =>
          message.error(e.response?.data?.message || '保存失败')
        )
        .finally(() => setSaving(false));
    });
  };

  return (
    <Card
      title="船讯网密钥（shipxy API）"
      loading={loading}
      extra={
        <Button type="primary" loading={saving} onClick={onSave}>
          保存
        </Button>
      }
    >
      <Space direction="vertical" size={12} style={{ width: '100%', maxWidth: 560 }}>
        <div>
          当前状态：
          {configured ? <Tag color="green">已配置 {maskedKey}</Tag> : <Tag>未配置</Tag>}
        </div>
        <Form form={form} layout="vertical">
          <Form.Item
            name="apiKey"
            label="API Key"
            extra={configured ? '已保存密钥，留空表示不修改；输入新密钥则覆盖' : '请在船讯网 shipxy.com 申请 API Key 后填入'}
          >
            <Input.Password placeholder={configured ? maskedKey ?? '已配置' : '请输入 shipxy API Key'} autoComplete="off" />
          </Form.Item>
        </Form>
        <Alert
          type="info"
          showIcon
          message="配置后，海运运单的船位抓取将优先调用 shipxy「单船位置」接口（按 MMSI，国内网络可达），失败时自动回落网页源；未配置时仅使用网页源。shipxy 接口按调用量计费，系统默认每船每 6 小时抓取一次。"
        />
      </Space>
    </Card>
  );
};

export default ShipxyConfigPage;
